import axios, { AxiosResponse } from "axios";
import { useQueryClient } from "@tanstack/react-query";

import { useUserStore } from "@store/user";
import { usersApi } from "@api/services";
import { Error } from "@helpers";
import { useGetAssignedCompanies } from "@api";

type AuthResponse = {
  getUser: () => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: (companyId?: string) => Promise<void>;
  refreshTokenRequest: (
    refreshToken: string,
    companyId?: string,
  ) => Promise<AxiosResponse>;
};

export const useAuth = (): AuthResponse => {
  const { setUser, setJwtTokenData } = useUserStore();
  const { refetchAssignedCompanies } = useGetAssignedCompanies();
  const queryClient = useQueryClient();

  const getUser = async (): Promise<void> => {
    const accessToken = localStorage.getItem("access_token");

    if (!accessToken) {
      setUser(undefined);

      return;
    }

    try {
      const response = await usersApi.usersControllerGetProfile();

      if (response.status === 200) {
        setUser(response.data);
        setJwtTokenData(accessToken);
        refetchAssignedCompanies();
      }
    } catch (e) {
      const error = e as import("axios").AxiosError;

      if (
        error?.response?.status === 401 &&
        error.config?.url?.endsWith("profile")
      ) {
        setUser(undefined);
      }

      Error(e);
    }
  };

  const logout = async (): Promise<void> => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    await queryClient.resetQueries();
    setUser(undefined);
  };

  const refreshTokenRequest = async (
    refreshToken: string,
    companyId?: string,
  ): Promise<AxiosResponse> => {
    return await axios({
      method: "POST",
      url: "/auth/refresh",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      data: new URLSearchParams({
        refresh_token: refreshToken,
        company_id: companyId || "",
      }),
    });
  };

  const refreshToken = async (companyId?: string): Promise<void> => {
    const storedRefreshToken = localStorage.getItem("refresh_token");

    if (storedRefreshToken) {
      try {
        const response = await refreshTokenRequest(storedRefreshToken, companyId);

        const { access_token, refresh_token } = response.data;

        localStorage.setItem("access_token", access_token);

        if (refresh_token) {
          localStorage.setItem("refresh_token", refresh_token);
        }

        window.dispatchEvent(new Event("access_token_change"));
      } catch (error) {
        Error("Error updating active company:", error);
      }
    }
  };

  return {
    getUser,
    logout,
    refreshToken,
    refreshTokenRequest,
  };
};
